import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { format, addDays, addWeeks, addMonths } from "date-fns";
import {
  ArrowLeft,
  ArrowRight,
  Calendar as CalendarIcon,
  Check,
  CreditCard,
  IndianRupee,
  Loader2,
  Mail,
  Plus,
  Send,
  Trash2,
  User,
  Wallet,
  CalendarDays,
  CalendarRange,
  RefreshCw,
  AlertCircle,
  Building2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatCurrency, Currency, getCurrencySymbol } from "@/lib/currency";
import { ProfessionalTerminology } from "@/components/ProfessionalSidebar";
import { BankDetailsDialog, checkBankDetailsConfigured } from "@/components/BankDetailsDialog";
import { useAuth } from "@/contexts/AuthContext";

// Steps for the wizard
const STEPS = [
  { id: 1, name: "Client", description: "Who to collect from" },
  { id: 2, name: "Details", description: "Amount & schedule" },
  { id: 3, name: "Review", description: "Confirm & send" },
];

// Frequency options
type FrequencyType = "one_time" | "weekly" | "monthly";

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string | null;
}

interface Tranche {
  id: string;
  label: string;
  amount: string;
  dueDate: Date | undefined;
}

interface CreateCollectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantId: string;
  currency: Currency;
  terminology?: ProfessionalTerminology;
  onSuccess?: () => void;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function CreateCollectionDialog({
  open,
  onOpenChange,
  merchantId,
  currency,
  terminology = {},
  onSuccess,
}: CreateCollectionDialogProps) {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Bank details gate
  const [hasBankDetails, setHasBankDetails] = useState<boolean | null>(null);
  const [checkingBankDetails, setCheckingBankDetails] = useState(false);
  const [showBankDetailsDialog, setShowBankDetailsDialog] = useState(false);

  // Client selection
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [newClient, setNewClient] = useState({ name: "", email: "", phone: "" });
  const [useNewClient, setUseNewClient] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Collection details
  const [collectionName, setCollectionName] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [dueDate, setDueDate] = useState<Date | undefined>(addDays(new Date(), 7));
  
  // Frequency selection (new)
  const [frequency, setFrequency] = useState<FrequencyType>("one_time");
  const [numberOfPayments, setNumberOfPayments] = useState(1);
  
  // Tranches (staggered payments)
  const [useTranches, setUseTranches] = useState(false);
  const [tranches, setTranches] = useState<Tranche[]>([
    { id: "1", label: "Upfront", amount: "", dueDate: new Date() },
  ]);

  const clientLabel = terminology.customers || "Client";

  // Check bank details when dialog opens
  useEffect(() => {
    const checkBankDetails = async () => {
      if (!open || !user?.email) return;
      
      setCheckingBankDetails(true);
      try {
        const configured = await checkBankDetailsConfigured(user.email);
        setHasBankDetails(configured);
      } catch (error) {
        console.error("Error checking bank details:", error);
        setHasBankDetails(false);
      } finally {
        setCheckingBankDetails(false);
      }
    };

    checkBankDetails();
  }, [open, user?.email]);

  // Fetch existing clients
  useEffect(() => {
    if (open && merchantId) {
      fetchClients();
    }
  }, [open, merchantId]);

  // Reset form when closed
  useEffect(() => {
    if (!open) {
      setCurrentStep(1);
      setSelectedClient(null);
      setNewClient({ name: "", email: "", phone: "" });
      setUseNewClient(false);
      setSearchQuery("");
      setCollectionName("");
      setDescription("");
      setAmount("");
      setDueDate(addDays(new Date(), 7));
      setFrequency("one_time");
      setNumberOfPayments(1);
      setUseTranches(false);
      setTranches([{ id: "1", label: "Upfront", amount: "", dueDate: new Date() }]);
      // Reset bank details check for next open
      setHasBankDetails(null);
    }
  }, [open]);

  // Handler for when bank details are saved successfully
  const handleBankDetailsSaved = () => {
    setHasBankDetails(true);
    setShowBankDetailsDialog(false);
    toast.success("Bank details saved! You can now create collections.");
  };

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const { data, error } = await supabase
        .from("professional_clients")
        .select("id, name, email, phone")
        .eq("merchant_id", merchantId)
        .eq("status", "active")
        .order("name");
      
      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoadingClients(false);
    }
  };

  const filteredClients = clients.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate total from tranches
  const tranchesTotal = tranches.reduce(
    (sum, t) => sum + (parseFloat(t.amount) || 0),
    0
  );

  // Validation
  const isStep1Valid = () => {
    if (useNewClient) {
      return newClient.name.trim() && newClient.email.trim() && newClient.email.includes("@");
    }
    return selectedClient !== null;
  };

  const isStep2Valid = () => {
    if (!collectionName.trim()) return false;
    if (useTranches) {
      return tranches.every(
        (t) => t.label.trim() && parseFloat(t.amount) > 0 && t.dueDate
      );
    }
    if (frequency !== "one_time" && numberOfPayments < 1) return false;
    return parseFloat(amount) > 0 && dueDate !== undefined;
  };

  // Generate recurring payment schedule
  const getRecurringSchedule = () => {
    if (frequency === "one_time" || !dueDate) return [];
    
    const schedule = [];
    const amountNum = parseFloat(amount) || 0;
    
    for (let i = 0; i < numberOfPayments; i++) {
      let paymentDate;
      if (frequency === "weekly") {
        paymentDate = addWeeks(dueDate, i);
      } else {
        paymentDate = addMonths(dueDate, i);
      }
      schedule.push({
        number: i + 1,
        date: paymentDate,
        amount: amountNum,
      });
    }
    return schedule;
  };

  // Add tranche
  const addTranche = () => {
    const nextNum = tranches.length + 1;
    setTranches([
      ...tranches,
      {
        id: String(Date.now()),
        label: `Installment ${nextNum - 1}`,
        amount: "",
        dueDate: tranches.length > 0 
          ? addDays(tranches[tranches.length - 1].dueDate || new Date(), 30) 
          : addDays(new Date(), 30),
      },
    ]);
  };

  // Remove tranche
  const removeTranche = (id: string) => {
    if (tranches.length > 1) {
      setTranches(tranches.filter((t) => t.id !== id));
    }
  };

  // Update tranche
  const updateTranche = (id: string, field: keyof Tranche, value: any) => {
    setTranches(
      tranches.map((t) => (t.id === id ? { ...t, [field]: value } : t))
    );
  };

  // Submit collection
  const handleSubmit = async () => {
    setIsSending(true);
    try {
      // 1. Create or get client
      let clientId: string;
      let clientEmail: string;
      let clientName: string;

      if (useNewClient) {
        // Create new client
        const { data: newClientData, error: clientError } = await supabase
          .from("professional_clients")
          .insert({
            merchant_id: merchantId,
            name: newClient.name.trim(),
            email: newClient.email.trim().toLowerCase(),
            phone: newClient.phone.trim() || null,
          })
          .select()
          .single();

        if (clientError) {
          if (clientError.code === "23505") {
            toast.error("A client with this email already exists. Please select from list.");
            return;
          }
          throw clientError;
        }
        clientId = newClientData.id;
        clientEmail = newClientData.email;
        clientName = newClientData.name;
      } else {
        clientId = selectedClient!.id;
        clientEmail = selectedClient!.email;
        clientName = selectedClient!.name;
      }

      // 2. Create subscription record
      const recurringSchedule = getRecurringSchedule();
      const totalAmount = useTranches 
        ? tranchesTotal 
        : frequency === "one_time" 
          ? parseFloat(amount) 
          : parseFloat(amount) * numberOfPayments;
          
      const firstDueDate = useTranches 
        ? tranches[0].dueDate 
        : dueDate;

      const { data: subscription, error: subError } = await supabase
        .from("client_subscriptions")
        .insert({
          client_id: clientId,
          merchant_id: merchantId,
          service_name: collectionName.trim(),
          description: description.trim() || null,
          frequency: useTranches ? "staggered" : frequency,
          amount: totalAmount,
          currency,
          start_date: format(new Date(), "yyyy-MM-dd"),
          next_due_date: firstDueDate ? format(firstDueDate, "yyyy-MM-dd") : null,
          tranches_config: useTranches 
            ? JSON.stringify(tranches.map(t => ({
                label: t.label,
                amount: parseFloat(t.amount),
                due_date: t.dueDate ? format(t.dueDate, "yyyy-MM-dd") : null,
              })))
            : null,
        })
        .select()
        .single();

      if (subError) throw subError;

      // 3. Create payment records and collect their IDs
      let createdPaymentIds: string[] = [];

      if (useTranches) {
        // Create a payment record for each tranche
        const paymentRecords = tranches.map((t, index) => ({
          subscription_id: subscription.id,
          client_id: clientId,
          merchant_id: merchantId,
          amount: parseFloat(t.amount),
          currency,
          due_date: t.dueDate ? format(t.dueDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
          tranche_number: index + 1,
          tranche_label: t.label,
          status: "pending",
        }));

        const { data: payments, error: paymentError } = await supabase
          .from("client_payments")
          .insert(paymentRecords)
          .select("id, amount, due_date, tranche_label, tranche_number");

        if (paymentError) throw paymentError;
        createdPaymentIds = payments?.map(p => p.id) || [];

        // Send email for the FIRST tranche only (upcoming payment)
        if (payments && payments.length > 0) {
          const firstPayment = payments[0];
          await sendPaymentLinkEmail(
            firstPayment.id,
            clientId,
            subscription.id,
            firstPayment.amount,
            firstPayment.due_date,
            firstPayment.tranche_label,
            firstPayment.tranche_number
          );
        }
      } else if (frequency !== "one_time" && recurringSchedule.length > 0) {
        // Create multiple payment records for recurring payments
        const paymentRecords = recurringSchedule.map((payment, index) => ({
          subscription_id: subscription.id,
          client_id: clientId,
          merchant_id: merchantId,
          amount: payment.amount,
          currency,
          due_date: format(payment.date, "yyyy-MM-dd"),
          tranche_number: payment.number,
          tranche_label: `${frequency === "weekly" ? "Week" : "Month"} ${payment.number}`,
          status: "pending",
        }));

        const { data: payments, error: paymentError } = await supabase
          .from("client_payments")
          .insert(paymentRecords)
          .select("id, amount, due_date, tranche_label, tranche_number");

        if (paymentError) throw paymentError;
        createdPaymentIds = payments?.map(p => p.id) || [];

        // Send email for the FIRST payment only
        if (payments && payments.length > 0) {
          const firstPayment = payments[0];
          await sendPaymentLinkEmail(
            firstPayment.id,
            clientId,
            subscription.id,
            firstPayment.amount,
            firstPayment.due_date,
            firstPayment.tranche_label,
            firstPayment.tranche_number
          );
        }
      } else {
        // Single one-time payment
        const { data: payment, error: paymentError } = await supabase
          .from("client_payments")
          .insert({
            subscription_id: subscription.id,
            client_id: clientId,
            merchant_id: merchantId,
            amount: parseFloat(amount),
            currency,
            due_date: dueDate ? format(dueDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
            status: "pending",
          })
          .select("id, amount, due_date")
          .single();

        if (paymentError) throw paymentError;
        createdPaymentIds = [payment.id];

        // Send email for this payment
        await sendPaymentLinkEmail(
          payment.id,
          clientId,
          subscription.id,
          payment.amount,
          payment.due_date,
          null,
          null
        );
      }

      toast.success(`Payment request created! Link sent to ${clientEmail}`);
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      console.error("Error creating collection:", error);
      toast.error(error.message || "Failed to create payment request");
    } finally {
      setIsSending(false);
    }
  };

  // Helper function to send payment link email
  const sendPaymentLinkEmail = async (
    paymentId: string,
    clientId: string,
    subscriptionId: string,
    paymentAmount: number,
    paymentDueDate: string,
    trancheLabel: string | null,
    trancheNumber: number | null
  ) => {
    try {
      const response = await supabase.functions.invoke("send-collection-payment-link", {
        body: {
          payment_id: paymentId,
          merchant_id: merchantId,
          client_id: clientId,
          subscription_id: subscriptionId,
          amount: paymentAmount,
          currency,
          due_date: paymentDueDate,
          collection_name: collectionName.trim(),
          description: description.trim() || undefined,
          tranche_label: trancheLabel,
          tranche_number: trancheNumber,
          include_invoice: true,
        },
      });

      if (response.error) {
        console.error("Error sending payment link email:", response.error);
      } else {
        console.log("Payment link email sent:", response.data);
      }
    } catch (err) {
      console.error("Failed to send payment link email:", err);
      // Non-blocking - collection was still created
    }
  };

  const progressPercent = (currentStep / STEPS.length) * 100;

  const getClient = () => {
    if (useNewClient) {
      return { name: newClient.name, email: newClient.email, phone: newClient.phone };
    }
    return selectedClient;
  };

  // Show BankDetailsDialog separately (not nested)
  if (showBankDetailsDialog) {
    return (
      <BankDetailsDialog
        open={showBankDetailsDialog}
        onOpenChange={(open) => {
          setShowBankDetailsDialog(open);
          // If user closes bank details dialog without saving, close main dialog too
          if (!open && !hasBankDetails) {
            onOpenChange(false);
          }
        }}
        organizerEmail={user?.email || ""}
        onSuccess={handleBankDetailsSaved}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Collection</DialogTitle>
            <DialogDescription>
              Create a payment request and send it to your {clientLabel.toLowerCase()}
            </DialogDescription>
          </DialogHeader>

          {/* Bank Details Gate - show if checking or missing */}
          {(checkingBankDetails || hasBankDetails === null || hasBankDetails === false) && (
            <div className="py-6">
              {(checkingBankDetails || hasBankDetails === null) ? (
                <div className="flex flex-col items-center justify-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
                  <p className="text-sm text-muted-foreground">Checking payment setup...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-sm">
                      <strong>Bank details required</strong>
                      <br />
                      Before you can collect payments, we need your bank account details to know where to send the funds.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="flex flex-col items-center gap-3 p-4 rounded-lg border-2 border-dashed border-muted-foreground/30">
                    <Building2 className="h-10 w-10 text-muted-foreground" />
                    <p className="text-center text-sm text-muted-foreground">
                      Add your bank account to receive payments via NEFT/IMPS
                    </p>
                    <Button
                      className="bg-cyan-600 hover:bg-cyan-700"
                      onClick={() => setShowBankDetailsDialog(true)}
                    >
                      <Building2 className="mr-2 h-4 w-4" />
                      Add Bank Details
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Main Content - only show if bank details are configured */}
          {hasBankDetails === true && (
            <>
              {/* Progress */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  {STEPS.map((step) => (
                    <div
                      key={step.id}
                      className={cn(
                        "flex items-center gap-1",
                        currentStep >= step.id
                          ? "text-cyan-600 font-medium"
                          : "text-muted-foreground"
                      )}
                    >
                      <div
                        className={cn(
                          "w-6 h-6 rounded-full flex items-center justify-center text-xs",
                          currentStep > step.id
                            ? "bg-cyan-600 text-white"
                            : currentStep === step.id
                            ? "bg-cyan-100 dark:bg-cyan-900 text-cyan-600"
                            : "bg-muted text-muted-foreground"
                        )}
                      >
                        {currentStep > step.id ? <Check className="w-3 h-3" /> : step.id}
                      </div>
                      <span className="hidden sm:inline">{step.name}</span>
                    </div>
                  ))}
                </div>
                <Progress value={progressPercent} className="h-1" />
              </div>

        {/* Step 1: Client Selection */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Button
                variant={!useNewClient ? "default" : "outline"}
                size="sm"
                onClick={() => setUseNewClient(false)}
                className={!useNewClient ? "bg-cyan-600 hover:bg-cyan-700" : ""}
              >
                Existing {clientLabel}
              </Button>
              <Button
                variant={useNewClient ? "default" : "outline"}
                size="sm"
                onClick={() => setUseNewClient(true)}
                className={useNewClient ? "bg-cyan-600 hover:bg-cyan-700" : ""}
              >
                New {clientLabel}
              </Button>
            </div>

            {!useNewClient ? (
              <div className="space-y-3">
                <Input
                  placeholder={`Search ${clientLabel.toLowerCase()}s...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {loadingClients ? (
                    <div className="text-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                    </div>
                  ) : filteredClients.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      {searchQuery ? "No clients found" : "No clients yet"}
                    </div>
                  ) : (
                    filteredClients.map((client) => (
                      <div
                        key={client.id}
                        onClick={() => setSelectedClient(client)}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                          selectedClient?.id === client.id
                            ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-950"
                            : "hover:bg-muted/50"
                        )}
                      >
                        <div className="h-9 w-9 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
                          <User className="h-4 w-4 text-cyan-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{client.name}</p>
                          <p className="text-sm text-muted-foreground truncate">
                            {client.email}
                          </p>
                        </div>
                        {selectedClient?.id === client.id && (
                          <Check className="h-4 w-4 text-cyan-600" />
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    placeholder="Enter client name"
                    value={newClient.name}
                    onChange={(e) =>
                      setNewClient({ ...newClient, name: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    placeholder="client@example.com"
                    value={newClient.email}
                    onChange={(e) =>
                      setNewClient({ ...newClient, email: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone (optional)</Label>
                  <Input
                    placeholder="+91 9876543210"
                    value={newClient.phone}
                    onChange={(e) =>
                      setNewClient({ ...newClient, phone: e.target.value })
                    }
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Collection Details */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Collection Name *</Label>
              <Input
                placeholder="e.g., Website Design Fee, Coaching Session"
                value={collectionName}
                onChange={(e) => setCollectionName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                placeholder="Add any notes for your client..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>

            {/* Frequency Tabs */}
            <div className="space-y-3">
              <Label>Payment Frequency</Label>
              <Tabs 
                value={useTranches ? "custom" : frequency} 
                onValueChange={(val) => {
                  if (val === "custom") {
                    setUseTranches(true);
                  } else {
                    setUseTranches(false);
                    setFrequency(val as FrequencyType);
                  }
                }}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-4 bg-muted">
                  <TabsTrigger value="one_time" className="gap-1 text-xs sm:text-sm data-[state=active]:bg-cyan-600 data-[state=active]:text-white">
                    <Wallet className="h-3.5 w-3.5 hidden sm:block" />
                    One-time
                  </TabsTrigger>
                  <TabsTrigger value="weekly" className="gap-1 text-xs sm:text-sm data-[state=active]:bg-cyan-600 data-[state=active]:text-white">
                    <CalendarDays className="h-3.5 w-3.5 hidden sm:block" />
                    Weekly
                  </TabsTrigger>
                  <TabsTrigger value="monthly" className="gap-1 text-xs sm:text-sm data-[state=active]:bg-cyan-600 data-[state=active]:text-white">
                    <CalendarRange className="h-3.5 w-3.5 hidden sm:block" />
                    Monthly
                  </TabsTrigger>
                  <TabsTrigger value="custom" className="gap-1 text-xs sm:text-sm data-[state=active]:bg-cyan-600 data-[state=active]:text-white">
                    <RefreshCw className="h-3.5 w-3.5 hidden sm:block" />
                    Custom
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Amount and Due Date for non-custom frequencies */}
            {!useTranches ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Amount per {frequency === "one_time" ? "payment" : "cycle"} *</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                        {getCurrencySymbol(currency)}
                      </span>
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="pl-8"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{frequency === "one_time" ? "Due Date" : "Start Date"} *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dueDate ? format(dueDate, "MMM d, yyyy") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={dueDate}
                          onSelect={setDueDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Number of payments for recurring */}
                {frequency !== "one_time" && (
                  <div className="space-y-2">
                    <Label>Number of payments</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        min={1}
                        max={52}
                        value={numberOfPayments}
                        onChange={(e) => setNumberOfPayments(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-24"
                      />
                      <span className="text-sm text-muted-foreground">
                        Total: {formatCurrency((parseFloat(amount) || 0) * numberOfPayments, currency)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Preview recurring schedule */}
                {frequency !== "one_time" && getRecurringSchedule().length > 0 && (
                  <Card className="bg-muted/30">
                    <CardContent className="p-3">
                      <p className="text-sm font-medium mb-2">Payment Schedule</p>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {getRecurringSchedule().slice(0, 6).map((payment) => (
                          <div key={payment.number} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Payment {payment.number}
                            </span>
                            <span>
                              {formatCurrency(payment.amount, currency)} • {format(payment.date, "MMM d, yyyy")}
                            </span>
                          </div>
                        ))}
                        {getRecurringSchedule().length > 6 && (
                          <p className="text-xs text-muted-foreground text-center pt-1">
                            + {getRecurringSchedule().length - 6} more payments
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {tranches.map((tranche, index) => (
                  <Card key={tranche.id} className="bg-muted/30">
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <Input
                          value={tranche.label}
                          onChange={(e) =>
                            updateTranche(tranche.id, "label", e.target.value)
                          }
                          placeholder="Label"
                          className="max-w-[150px] h-8 text-sm"
                        />
                        {tranches.length > 1 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => removeTranche(tranche.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">
                            {getCurrencySymbol(currency)}
                          </span>
                          <Input
                            type="number"
                            placeholder="Amount"
                            value={tranche.amount}
                            onChange={(e) =>
                              updateTranche(tranche.id, "amount", e.target.value)
                            }
                            className="pl-6 h-8 text-sm"
                          />
                        </div>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full justify-start font-normal h-8 text-xs"
                            >
                              <CalendarIcon className="mr-1 h-3 w-3" />
                              {tranche.dueDate
                                ? format(tranche.dueDate, "MMM d")
                                : "Date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={tranche.dueDate}
                              onSelect={(date) =>
                                updateTranche(tranche.id, "dueDate", date)
                              }
                              disabled={(date) => date < new Date()}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addTranche}
                  className="w-full gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Add Installment
                </Button>
                {tranchesTotal > 0 && (
                  <div className="text-right text-sm">
                    Total: <span className="font-semibold">{formatCurrency(tranchesTotal, currency)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Review */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4 space-y-3">
                {/* Client info */}
                <div className="flex items-center gap-3 pb-3 border-b">
                  <div className="h-10 w-10 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
                    <User className="h-5 w-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="font-medium">{getClient()?.name}</p>
                    <p className="text-sm text-muted-foreground">{getClient()?.email}</p>
                  </div>
                </div>

                {/* Collection details */}
                <div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Collection</p>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 capitalize">
                      {useTranches ? "Custom" : frequency === "one_time" ? "One-time" : frequency}
                    </span>
                  </div>
                  <p className="font-medium">{collectionName}</p>
                  {description && (
                    <p className="text-sm text-muted-foreground mt-1">{description}</p>
                  )}
                </div>

                {/* Amount */}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    {useTranches || frequency === "one_time" ? "Total Amount" : `${numberOfPayments} ${frequency === "weekly" ? "weeks" : "months"}`}
                  </span>
                  <span className="text-lg font-semibold text-cyan-600">
                    {formatCurrency(
                      useTranches 
                        ? tranchesTotal 
                        : frequency === "one_time" 
                          ? parseFloat(amount) || 0 
                          : (parseFloat(amount) || 0) * numberOfPayments, 
                      currency
                    )}
                  </span>
                </div>

                {/* Schedule */}
                {useTranches ? (
                  <div className="space-y-2 pt-2 border-t">
                    <p className="text-sm text-muted-foreground">Payment Schedule</p>
                    {tranches.map((t, i) => (
                      <div key={t.id} className="flex justify-between text-sm">
                        <span>{t.label}</span>
                        <span>
                          {formatCurrency(parseFloat(t.amount) || 0, currency)}
                          {t.dueDate && ` • ${format(t.dueDate, "MMM d, yyyy")}`}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : frequency !== "one_time" ? (
                  <div className="space-y-2 pt-2 border-t">
                    <p className="text-sm text-muted-foreground">Payment Schedule</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {getRecurringSchedule().slice(0, 4).map((payment) => (
                        <div key={payment.number} className="flex justify-between text-sm">
                          <span>{frequency === "weekly" ? `Week ${payment.number}` : `Month ${payment.number}`}</span>
                          <span>
                            {formatCurrency(payment.amount, currency)} • {format(payment.date, "MMM d, yyyy")}
                          </span>
                        </div>
                      ))}
                      {getRecurringSchedule().length > 4 && (
                        <p className="text-xs text-muted-foreground text-center pt-1">
                          + {getRecurringSchedule().length - 4} more payments
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-center text-sm pt-2 border-t">
                    <span className="text-muted-foreground">Due Date</span>
                    <span>{dueDate ? format(dueDate, "MMM d, yyyy") : "—"}</span>
                  </div>
                )}

                {/* Payment method note */}
                <div className="flex items-center gap-2 pt-3 border-t text-sm text-muted-foreground">
                  {currency === "INR" ? (
                    <>
                      <IndianRupee className="h-4 w-4" />
                      Payment via Razorpay (UPI, Cards, Net Banking)
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4" />
                      Payment via Stripe (Cards)
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="flex items-start gap-2 p-3 rounded-lg bg-cyan-50 dark:bg-cyan-950/30 text-sm">
              <Mail className="h-4 w-4 text-cyan-600 mt-0.5" />
              <div>
                <p className="font-medium text-cyan-800 dark:text-cyan-200">
                  Email will be sent
                </p>
                <p className="text-cyan-700 dark:text-cyan-300">
                  Your client will receive a payment link at {getClient()?.email}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <DialogFooter className="flex-row gap-2 sm:gap-2">
          {currentStep > 1 && (
            <Button
              variant="outline"
              onClick={() => setCurrentStep(currentStep - 1)}
              disabled={isSending}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          )}
          <div className="flex-1" />
          {currentStep < STEPS.length ? (
            <Button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={
                (currentStep === 1 && !isStep1Valid()) ||
                (currentStep === 2 && !isStep2Valid())
              }
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              Continue
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSending}
              className="bg-cyan-600 hover:bg-cyan-700 gap-2"
            >
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send Payment Request
                </>
              )}
            </Button>
          )}
        </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
